import {
  Button,
  Checkbox,
  Group,
  Modal,
  Paper,
  PasswordInput,
  Select,
  SimpleGrid,
  Stack,
  Table,
  Text,
  Textarea,
  TextInput,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { notifications } from "@mantine/notifications";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { PhoneInput } from "../../components/PhoneInput";
import { CitySelect } from "../../components/CitySelect";
import { ConfirmDialog, DataTableShell, LoadingOverlay, PageHeader, SortableTh, StatusBadge, useInfinitePage, useTableSort } from "../../components/ui";
import { adminApi } from "../../lib/api";
import { ApiError } from "../../lib/apiClient";
import { COMPANY_KYC_SLOT_LABELS, ORG_KYC_COPY } from "../../lib/companyKyc";
import { ACCESS_GRANT_COPY } from "../../lib/confirmDialog";
import { CITY_SELECT_COPY } from "../../lib/cities";
import { NAV_COPY, NAV_ICONS } from "../../lib/navCopy";
import { formatDate } from "../../lib/format";
import { validateIndianPhoneFieldOptional, toPhoneForApi } from "../../lib/phone";
import type { Company, CompanyType } from "../../types/api";

function CompanyKycSlotImage({ companyId, slot }: { companyId: string; slot: string }) {
  const [url, setUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let objectUrl: string | null = null;
    let cancelled = false;
    adminApi
      .fetchCompanyKycFile(companyId, slot)
      .then((blob) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setUrl(objectUrl);
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : "Could not load image");
      });
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [companyId, slot]);

  if (error) {
    return (
      <Text size="sm" c="red">
        {error}
      </Text>
    );
  }
  if (!url) {
    return <LoadingOverlay height="var(--axio-unit-160)" />;
  }
  return (
    <img
      src={url}
      alt={COMPANY_KYC_SLOT_LABELS[slot] ?? slot}
      style={{ width: "var(--axio-percentage-full)", maxHeight: "var(--axio-unit-240)", objectFit: "contain" }}
    />
  );
}

export function CompaniesPage({ companyType }: { companyType: CompanyType }) {
  const queryClient = useQueryClient();
  const isConsultancy = companyType === "consultancy";
  const [createOpen, setCreateOpen] = useState(false);
  const [addHrCompany, setAddHrCompany] = useState<Company | null>(null);
  const [managersCompany, setManagersCompany] = useState<Company | null>(null);
  const [reviewCompany, setReviewCompany] = useState<Company | null>(null);
  const [reviewNotes, setReviewNotes] = useState("");
  const [confirmAction, setConfirmAction] = useState<
    { kind: "verifyWithoutDocs" | "reject"; company: Company } | null
  >(null);
  const [managerUserId, setManagerUserId] = useState<string | null>(null);
  const [managerPrimary, setManagerPrimary] = useState(false);

  const companiesQuery = useInfinitePage(["admin-companies", companyType], (page, pageSize) =>
    adminApi.listCompanies({ company_type: companyType, page, page_size: pageSize }),
  );

  const reviewKycQuery = useQuery({
    queryKey: ["admin-company-kyc", reviewCompany?.id],
    queryFn: () => adminApi.getCompanyKyc(reviewCompany!.id),
    enabled: !!reviewCompany,
  });

  const verifyMutation = useMutation({
    mutationFn: ({ id, status, notes }: { id: string; status: "verified" | "rejected"; notes?: string }) =>
      adminApi.verifyCompany(id, { status, notes }),
    onSuccess: () => {
      notifications.show({ color: "green", title: "Updated", message: "Company status updated." });
      queryClient.invalidateQueries({ queryKey: ["admin-companies"] });
      queryClient.invalidateQueries({ queryKey: ["admin-company-kyc"] });
      setReviewCompany(null);
      setReviewNotes("");
      setConfirmAction(null);
    },
    onError: (err) => {
      const message = err instanceof ApiError ? err.message : "Something went wrong";
      notifications.show({ color: "red", title: "Failed", message });
    },
  });

  const createForm = useForm({
    initialValues: {
      legal_name: "",
      display_name: "",
      city: "",
      hr_email: "",
      hr_password: "",
      hr_name: "",
      hr_phone: "",
      company_type: companyType,
    },
    validate: {
      legal_name: (v) => (v.trim().length >= 2 ? null : "Required"),
      display_name: (v) => (v.trim().length >= 2 ? null : "Required"),
      company_type: (v) => (v ? null : "Required"),
      city: (v) => (!isConsultancy || v.trim() ? null : CITY_SELECT_COPY.requiredError),
      hr_email: (v) => (/^\S+@\S+\.\S+$/.test(v) ? null : "Valid email required"),
      hr_password: (v) => (v.length >= 8 ? null : "Min 8 characters"),
      hr_name: (v) => (v.trim().length >= 2 ? null : "Required"),
      hr_phone: validateIndianPhoneFieldOptional,
    },
  });

  const addHrForm = useForm({
    initialValues: {
      full_name: "",
      email: "",
      password: "",
      phone: "",
    },
    validate: {
      full_name: (v) => (v.trim().length >= 2 ? null : "Required"),
      email: (v) => (/^\S+@\S+\.\S+$/.test(v) ? null : "Valid email required"),
      password: (v) => (v.length >= 8 ? null : "Min 8 characters"),
      phone: validateIndianPhoneFieldOptional,
    },
  });

  const createMutation = useMutation({
    mutationFn: adminApi.createCompany,
    onSuccess: () => {
      notifications.show({ color: "green", title: "Created", message: "Company and HR user created." });
      queryClient.invalidateQueries({ queryKey: ["admin-companies"] });
      createForm.reset();
      setCreateOpen(false);
    },
    onError: (err) => {
      const message = err instanceof ApiError ? err.message : "Something went wrong";
      notifications.show({ color: "red", title: "Failed", message });
    },
  });

  const addHrMutation = useMutation({
    mutationFn: ({
      companyId,
      payload,
    }: {
      companyId: string;
      payload: { email: string; password: string; full_name: string; phone?: string };
    }) =>
      adminApi.addCompanyUser(companyId, {
        ...payload,
        company_role: addHrCompany?.company_type === "consultancy" ? "company_admin" : "hr",
      }),
    onSuccess: () => {
      notifications.show({ color: "green", title: "HR added", message: "HR user created for company." });
      addHrForm.reset();
      setAddHrCompany(null);
    },
    onError: (err) => {
      const message = err instanceof ApiError ? err.message : "Something went wrong";
      notifications.show({ color: "red", title: "Failed", message });
    },
  });

  const adminsQuery = useQuery({
    queryKey: ["admin-users", "admin"],
    queryFn: () => adminApi.listUsers({ role: "admin", page_size: 100 }),
    enabled: !!managersCompany,
  });

  const managersQuery = useQuery({
    queryKey: ["account-managers", managersCompany?.id],
    queryFn: () => adminApi.listAccountManagers(managersCompany!.id),
    enabled: !!managersCompany,
  });

  const addManagerMutation = useMutation({
    mutationFn: () =>
      adminApi.addAccountManager(managersCompany!.id, {
        admin_user_id: managerUserId!,
        is_primary: managerPrimary,
      }),
    onSuccess: () => {
      notifications.show({ color: "green", message: "Account manager assigned." });
      queryClient.invalidateQueries({ queryKey: ["account-managers", managersCompany?.id] });
      queryClient.invalidateQueries({ queryKey: ["admin-companies"] });
      setManagerUserId(null);
      setManagerPrimary(false);
    },
    onError: (err) => {
      notifications.show({
        color: "red",
        message: err instanceof ApiError ? err.message : "Failed to assign",
      });
    },
  });

  const removeManagerMutation = useMutation({
    mutationFn: (linkId: string) => adminApi.removeAccountManager(managersCompany!.id, linkId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["account-managers", managersCompany?.id] });
      queryClient.invalidateQueries({ queryKey: ["admin-companies"] });
    },
  });

  const companies = companiesQuery.items;
  const { sorted: sortedCompanies, sortKey, direction, toggle } = useTableSort(companies, {
    company: (r) => r.display_name,
    type: (r) => r.company_type ?? "client",
    city: (r) => r.city,
    status: (r) => r.verification_status,
    created: (r) => r.created_at,
  });

  return (
    <Stack gap="lg">
      <PageHeader
        icon={isConsultancy ? NAV_ICONS.consultancies : NAV_ICONS.clients}
        title={isConsultancy ? NAV_COPY.admin.consultancies : NAV_COPY.admin.clients}
        subtitle={
          isConsultancy
            ? "Verify consultancies so they can manage recruiters and rate cards."
            : "Verify client companies so HR can publish jobs. Create companies and HR accounts."
        }
        actions={
          <Button
            color="violet"
            onClick={() => {
              createForm.setFieldValue("company_type", companyType);
              setCreateOpen(true);
            }}
          >
            + Create company
          </Button>
        }
      />

      <DataTableShell
        loading={companiesQuery.isLoading}
        error={
          companiesQuery.isError
            ? companiesQuery.error instanceof Error
              ? companiesQuery.error.message
              : undefined
            : undefined
        }
        onRetry={() => void companiesQuery.refetch()}
        hasMore={companiesQuery.hasMore}
        loadingMore={companiesQuery.loadingMore}
        onLoadMore={companiesQuery.loadMore}
        isEmpty={companies.length === 0}
        empty={{
          icon: isConsultancy ? NAV_ICONS.consultancies : NAV_ICONS.clients,
          title: isConsultancy ? "No consultancy companies yet" : "No client companies yet",
          description: "Create a company to onboard HR users.",
        }}
      >
        <Table verticalSpacing="md" highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <SortableTh columnKey="company" sortKey={sortKey} direction={direction} onSort={toggle}>
                Company
              </SortableTh>
              <SortableTh columnKey="type" sortKey={sortKey} direction={direction} onSort={toggle}>
                Type
              </SortableTh>
              <SortableTh columnKey="city" sortKey={sortKey} direction={direction} onSort={toggle}>
                City
              </SortableTh>
              <SortableTh columnKey="status" sortKey={sortKey} direction={direction} onSort={toggle}>
                Status
              </SortableTh>
              <SortableTh columnKey="created" sortKey={sortKey} direction={direction} onSort={toggle}>
                Created
              </SortableTh>
              <Table.Th />
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {sortedCompanies.map((c: Company) => (
              <Table.Tr key={c.id}>
                <Table.Td fw="var(--axio-font-weight-medium)">{c.display_name}</Table.Td>
                <Table.Td>{c.company_type === "consultancy" ? "Consultancy" : "Client"}</Table.Td>
                <Table.Td>{c.city ?? "—"}</Table.Td>
                <Table.Td>
                  <StatusBadge status={c.verification_status} />
                </Table.Td>
                <Table.Td>{formatDate(c.created_at)}</Table.Td>
                <Table.Td>
                  <Group gap="xs">
                    <Button size="xs" variant="default" onClick={() => setAddHrCompany(c)}>
                      {c.company_type === "consultancy" ? "Add admin" : "Add HR"}
                    </Button>
                    <Button size="xs" variant="default" onClick={() => setManagersCompany(c)}>
                      Account managers
                    </Button>
                    {c.company_type === "consultancy" ? (
                      <Button
                        size="xs"
                        variant="default"
                        component="a"
                        href={`/admin/companies/${c.id}/rate-cards`}
                      >
                        Rate cards
                      </Button>
                    ) : (
                      <Button
                        size="xs"
                        variant="default"
                        component="a"
                        href={`/admin/companies/${c.id}/contracts`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Contracts
                      </Button>
                    )}
                    {c.verification_status !== "verified" && (
                      <>
                        <Button size="xs" variant="default" onClick={() => setReviewCompany(c)}>
                          {ORG_KYC_COPY.reviewPack}
                        </Button>
                        <Button
                          size="xs"
                          color="green"
                          variant="light"
                          onClick={() => setConfirmAction({ kind: "verifyWithoutDocs", company: c })}
                        >
                          {ORG_KYC_COPY.verifyWithoutDocs}
                        </Button>
                        <Button
                          size="xs"
                          color="red"
                          variant="light"
                          onClick={() => setConfirmAction({ kind: "reject", company: c })}
                        >
                          {ACCESS_GRANT_COPY.rejectCta}
                        </Button>
                      </>
                    )}
                  </Group>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </DataTableShell>

      <Modal
        opened={createOpen}
        onClose={() => setCreateOpen(false)}
        title={isConsultancy ? "Create consultancy + admin user" : "Create company + HR user"}
        size="lg"
        centered
      >
        <form
          onSubmit={createForm.onSubmit((values) =>
            createMutation.mutate({
              legal_name: values.legal_name,
              display_name: values.display_name,
              city: values.city || undefined,
              company_type: values.company_type,
              hr_user: {
                email: values.hr_email,
                password: values.hr_password,
                full_name: values.hr_name,
                phone: values.hr_phone ? toPhoneForApi(values.hr_phone) : undefined,
                company_role: "company_admin",
              },
            }),
          )}
        >
          <Stack>
            <TextInput label="Legal name" required {...createForm.getInputProps("legal_name")} />
            <TextInput label="Display name" required {...createForm.getInputProps("display_name")} />
            <CitySelect
              label={CITY_SELECT_COPY.label}
              withAsterisk={isConsultancy}
              clearable={!isConsultancy}
              value={createForm.values.city}
              onChange={(city) => createForm.setFieldValue("city", city)}
              error={createForm.errors.city}
            />
            <Paper withBorder p="md">
              <Text fw="var(--axio-font-weight-semibold)" mb="sm">
                First user (company admin)
              </Text>
              <Stack gap="sm">
                <TextInput label="Full name" required {...createForm.getInputProps("hr_name")} />
                <TextInput label="Email" required {...createForm.getInputProps("hr_email")} />
                <PhoneInput
                  label="Phone (WhatsApp)"
                  value={createForm.values.hr_phone}
                  onChange={(value) => createForm.setFieldValue("hr_phone", value)}
                  error={createForm.errors.phone}
                />
                <PasswordInput label="Password" required {...createForm.getInputProps("hr_password")} />
              </Stack>
            </Paper>
            <Button type="submit" color="violet" loading={createMutation.isPending}>
              Create
            </Button>
          </Stack>
        </form>
      </Modal>

      <Modal
        opened={!!addHrCompany}
        onClose={() => {
          setAddHrCompany(null);
          addHrForm.reset();
        }}
        title={`${addHrCompany?.company_type === "consultancy" ? "Add admin" : "Add HR"} — ${addHrCompany?.display_name ?? ""}`}
        centered
      >
        <form
          onSubmit={addHrForm.onSubmit((values) => {
            if (!addHrCompany) return;
            addHrMutation.mutate({
              companyId: addHrCompany.id,
              payload: {
                full_name: values.full_name,
                email: values.email,
                password: values.password,
                phone: values.phone ? toPhoneForApi(values.phone) : undefined,
              },
            });
          })}
        >
          <Stack>
            <TextInput label="Full name" required {...addHrForm.getInputProps("full_name")} />
            <TextInput label="Email" required {...addHrForm.getInputProps("email")} />
            <PhoneInput
              label="Phone (WhatsApp)"
              value={addHrForm.values.phone}
              onChange={(value) => addHrForm.setFieldValue("phone", value)}
              error={addHrForm.errors.phone}
            />
            <PasswordInput label="Password" required {...addHrForm.getInputProps("password")} />
            <Button type="submit" color="violet" loading={addHrMutation.isPending}>
              {addHrCompany?.company_type === "consultancy" ? "Create admin user" : "Create HR user"}
            </Button>
          </Stack>
        </form>
      </Modal>

      <Modal
        opened={!!managersCompany}
        onClose={() => {
          setManagersCompany(null);
          setManagerUserId(null);
          setManagerPrimary(false);
        }}
        title={`Account managers — ${managersCompany?.display_name ?? ""}`}
        centered
      >
        <Stack>
          {(managersQuery.data ?? []).map((link) => (
            <Group key={link.id} justify="space-between">
              <Text size="sm">
                {link.user.full_name} ({link.user.email})
                {link.is_primary ? " · primary" : ""}
              </Text>
              <Button
                size="xs"
                color="red"
                variant="light"
                loading={removeManagerMutation.isPending}
                onClick={() => removeManagerMutation.mutate(link.id)}
              >
                Remove
              </Button>
            </Group>
          ))}
          {(managersQuery.data ?? []).length === 0 && (
            <Text size="sm" c="dimmed">
              No account managers assigned yet.
            </Text>
          )}
          <Select
            label="Assign admin"
            placeholder="Select a platform admin"
            searchable
            data={(adminsQuery.data?.items ?? []).map((u) => ({
              value: u.id,
              label: `${u.full_name} (${u.email})`,
            }))}
            value={managerUserId}
            onChange={setManagerUserId}
          />
          <Checkbox
            label="Primary account manager"
            checked={managerPrimary}
            onChange={(e) => setManagerPrimary(e.currentTarget.checked)}
          />
          <Button
            disabled={!managerUserId}
            loading={addManagerMutation.isPending}
            onClick={() => addManagerMutation.mutate()}
          >
            Assign
          </Button>
        </Stack>
      </Modal>

      <Modal
        opened={!!reviewCompany}
        onClose={() => {
          setReviewCompany(null);
          setReviewNotes("");
        }}
        title={ORG_KYC_COPY.reviewPack}
        centered
        size="lg"
      >
        <Stack gap="md">
          {reviewKycQuery.isLoading ? (
            <LoadingOverlay height="var(--axio-unit-180)" />
          ) : (
            <>
              <Text size="sm">GSTIN: {reviewKycQuery.data?.gstin ?? "—"}</Text>
              <Text size="sm">
                {ORG_KYC_COPY.companyPanLabel}: {reviewKycQuery.data?.company_pan ?? "—"}
              </Text>
              <Text size="sm">
                {ORG_KYC_COPY.signatoryNameLabel}: {reviewKycQuery.data?.authorized_signatory_name ?? "—"}
              </Text>
              <Text size="sm">
                {ORG_KYC_COPY.signatoryPanLabel}: {reviewKycQuery.data?.authorized_signatory_pan ?? "—"}
              </Text>
              {(reviewKycQuery.data?.files ?? []).length === 0 ? (
                <Text size="sm" c="dimmed">
                  {ORG_KYC_COPY.verifyWithoutDocsHint}
                </Text>
              ) : (
                <SimpleGrid cols={{ base: 1, sm: 2 }}>
                  {(reviewKycQuery.data?.files ?? []).map((file) => (
                    <Paper key={file.slot} withBorder p="md">
                      <Stack gap="sm">
                        <Text fw="var(--axio-font-weight-semibold)" size="sm">
                          {COMPANY_KYC_SLOT_LABELS[file.slot] ?? file.slot}
                        </Text>
                        <CompanyKycSlotImage companyId={reviewCompany!.id} slot={file.slot} />
                      </Stack>
                    </Paper>
                  ))}
                </SimpleGrid>
              )}
              <Textarea label="Notes" value={reviewNotes} onChange={(e) => setReviewNotes(e.currentTarget.value)} />
              <Group>
                <Button
                  color="green"
                  loading={verifyMutation.isPending}
                  onClick={() =>
                    verifyMutation.mutate({
                      id: reviewCompany!.id,
                      status: "verified",
                      notes: reviewNotes || undefined,
                    })
                  }
                >
                  Verify
                </Button>
                <Button
                  color="red"
                  variant="light"
                  onClick={() =>
                    reviewCompany && setConfirmAction({ kind: "reject", company: reviewCompany })
                  }
                >
                  {ACCESS_GRANT_COPY.rejectCta}
                </Button>
              </Group>
            </>
          )}
        </Stack>
      </Modal>

      <ConfirmDialog
        opened={confirmAction?.kind === "verifyWithoutDocs"}
        onClose={() => setConfirmAction(null)}
        title={ACCESS_GRANT_COPY.verifyWithoutDocsTitle}
        description={
          <Text size="sm">
            <Text span fw="var(--axio-font-weight-semibold)">
              {confirmAction?.company.display_name}
            </Text>
            {". "}
            {ACCESS_GRANT_COPY.verifyWithoutDocsBody}
          </Text>
        }
        confirmLabel={ACCESS_GRANT_COPY.verifyWithoutDocsCta}
        confirmColor="green"
        loading={verifyMutation.isPending}
        onConfirm={() => {
          if (!confirmAction) return;
          verifyMutation.mutate({ id: confirmAction.company.id, status: "verified" });
        }}
      />

      <ConfirmDialog
        opened={confirmAction?.kind === "reject"}
        onClose={() => setConfirmAction(null)}
        title={ACCESS_GRANT_COPY.rejectCompanyTitle}
        description={
          <Text size="sm">
            <Text span fw="var(--axio-font-weight-semibold)">
              {confirmAction?.company.display_name}
            </Text>
            {". "}
            {ACCESS_GRANT_COPY.rejectCompanyBody}
          </Text>
        }
        confirmLabel={ACCESS_GRANT_COPY.rejectCompanyCta}
        loading={verifyMutation.isPending}
        onConfirm={() => {
          if (!confirmAction) return;
          verifyMutation.mutate({
            id: confirmAction.company.id,
            status: "rejected",
            notes: reviewNotes || undefined,
          });
        }}
      />
    </Stack>
  );
}
